import { createSelector } from 'reselect';
import { getCurrentEnterpriseId } from '@mews-commander/core';
import { RouteName } from '@mews-commander/enums';
import { getResolveRoute } from '@mews-commander/legacy-app-instance';
import { getCurrentEnterpriseUserPreferredServiceId } from '@mews-commander/legacy-entities/src/selectors/session/enterprise';
import { hasCurrentEnterpriseSsoAccessOnly } from '@mews-commander/legacy-entities/src/selectors/session/enterpriseProductSubscriptions';
import { isCurrentEnterprisePortfolio } from '@mews-commander/legacy-entities/src/selectors/session/subscription';
import { ApplicationState } from '@mews-commander/types';
import { getProp, getStringProp } from '@mews/fp';
import { ResolveRoute } from '@mews/routing';
import { NewReservationScreenUrlParamsKeys } from '../modules/newReservation/schemas/urlParamsSchema';

interface GetReservationUrlParams {
    ReleasedUtc: string;
    GroupId: string;
    CustomerId: string;
    StartUtc: string;
    EndUtc: string;
    State: string;
    Origin: string;
    Purpose: string;
    PartnerCompanyId: string;
    TravelAgencyId: string;
    BookerId: string;
    ProductIds: string;
    ServiceId: string;
    AvailabilityBlockId: string;
}

export const getReservationUrl = createSelector(
    getResolveRoute,
    (state: ApplicationState, optionalParams: Partial<GetReservationUrlParams>) => optionalParams || {},
    (
        resolveRoute,
        {
            ReleasedUtc,
            GroupId,
            CustomerId,
            StartUtc,
            EndUtc,
            State,
            Origin,
            Purpose,
            PartnerCompanyId,
            TravelAgencyId,
            BookerId,
            ProductIds,
            ServiceId,
            AvailabilityBlockId,
        }
    ) =>
        resolveRoute(RouteName.ReservationCreate, {
            query: {
                ...(ReleasedUtc && { [NewReservationScreenUrlParamsKeys.ReleasedUtc]: ReleasedUtc }),
                ...(GroupId && { [NewReservationScreenUrlParamsKeys.GroupId]: GroupId }),
                ...(CustomerId && { [NewReservationScreenUrlParamsKeys.CustomerId]: CustomerId }),
                ...(StartUtc && { [NewReservationScreenUrlParamsKeys.StartUtc]: StartUtc }),
                ...(EndUtc && { [NewReservationScreenUrlParamsKeys.EndUtc]: EndUtc }),
                ...(State && { [NewReservationScreenUrlParamsKeys.State]: State }),
                ...(Origin && { [NewReservationScreenUrlParamsKeys.Origin]: Origin }),
                ...(Purpose && { [NewReservationScreenUrlParamsKeys.Purpose]: Purpose }),
                ...(PartnerCompanyId && { [NewReservationScreenUrlParamsKeys.PartnerCompanyId]: PartnerCompanyId }),
                ...(TravelAgencyId && { [NewReservationScreenUrlParamsKeys.TravelAgencyId]: TravelAgencyId }),
                ...(BookerId && { [NewReservationScreenUrlParamsKeys.BookerId]: BookerId }),
                ...(ProductIds && { [NewReservationScreenUrlParamsKeys.ProductIds]: ProductIds }),
                ...(ServiceId && { [NewReservationScreenUrlParamsKeys.ServiceId]: ServiceId }),
                ...(AvailabilityBlockId && {
                    [NewReservationScreenUrlParamsKeys.AvailabilityBlockId]: AvailabilityBlockId,
                }),
            },
        })
);

export const getCustomerCreateUrl = createSelector(
    getResolveRoute,
    getStringProp('chainId'),
    getStringProp('lastName'),
    (resolveRoute, chainId, lastName) =>
        resolveRoute(RouteName.CustomerCreate, {
            query: {
                RedirectToProfile: true,
                ChainId: chainId,
                LastName: lastName,
            },
        })
);

export const getServiceReportArchiveUrl = createSelector(
    getProp<ResolveRoute>()('resolveRoute'),
    getStringProp('enterpriseId'),
    getStringProp('serviceId'),
    (resolveRoute, enterpriseId, serviceId) =>
        resolveRoute(RouteName.ServiceReportArchive, { query: { EnterpriseId: enterpriseId, ServiceId: serviceId } })
);

export const getServiceReportOrdersToProcessUrl = createSelector(
    getProp<ResolveRoute>()('resolveRoute'),
    getStringProp('enterpriseId'),
    getStringProp('serviceId'),
    (resolveRoute, enterpriseId, serviceId) =>
        resolveRoute(RouteName.ServiceReportOrdersToProcess, {
            query: { EnterpriseId: enterpriseId, ServiceId: serviceId },
        })
);

export const getReservationOverviewUrl = createSelector(
    getStringProp('mode'),
    getResolveRoute,
    getCurrentEnterpriseUserPreferredServiceId,
    (mode, resolveRoute, serviceId) =>
        resolveRoute(RouteName.ReservationOverviewReportIndex, {
            query: { mode: mode || '', 'Service.Id': serviceId },
        })
);

const getDashboardUrl = createSelector(getProp<ResolveRoute>()('resolveRoute'), (resolveRoute) =>
    resolveRoute(RouteName.DashboardIndex)
);

const getEnterpriseRelationshipReportUrl = createSelector(
    getProp<ResolveRoute>()('resolveRoute'),
    getCurrentEnterpriseId,
    (resolveRoute, enterpriseId) =>
        resolveRoute(RouteName.EnterpriseRelationshipReportIndex, {
            query: {
                EnterpriseId: enterpriseId,
            },
        })
);

const getUserGroupsUrl = createSelector(getProp<ResolveRoute>()('resolveRoute'), (resolveRoute) =>
    resolveRoute(RouteName.UserGroupsListIndex)
);

export const getHomeUrl = createSelector(
    isCurrentEnterprisePortfolio,
    getDashboardUrl,
    getEnterpriseRelationshipReportUrl,
    hasCurrentEnterpriseSsoAccessOnly,
    getUserGroupsUrl,
    (isPortfolio, dashboardUrl, portfolioUrl, hasSsoAccessOnly, userGroupsUrl) => {
        if (isPortfolio) {
            return hasSsoAccessOnly ? userGroupsUrl : portfolioUrl;
        }

        return dashboardUrl;
    }
);
